import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Input, 
  Select, 
  Form, 
  Tag, 
  Space, 
  Popconfirm, 
  message, 
  Tabs, 
  List,
  Tooltip, 
  Avatar, 
  Row, 
  Col,
  Badge,
  Divider,
  Progress,
  DatePicker,
  InputNumber,
  Statistic,
  Empty,
  Spin
} from 'antd';
import { 
  ProjectOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CalendarOutlined, 
  TeamOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ArrowLeftOutlined,
  TrophyOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { projectsConfig } from '../config/projects';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import dayjs from 'dayjs';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Projects = () => {
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const [projects, setProjects] = useState([]);
  const [projectProgress, setProjectProgress] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [activeTab, setActiveTab] = useState('projects');
  const [form] = Form.useForm();
  const [progressForm] = Form.useForm();

  // 加载项目数据
  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(projectsConfig.apiEndpoints.projects);
      if (response.data.success) {
        setProjects(response.data.projects || []);
      } else {
        message.error(response.data.message || '获取项目数据失败');
      }
    } catch (error) {
      console.error('获取项目数据失败:', error);
      message.error('获取项目数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载项目进度
  const fetchProjectProgress = async (projectId) => {
    try {
      const response = await axios.get(`${projectsConfig.apiEndpoints.progress}/${projectId}`);
      if (response.data.success) {
        setProjectProgress(prev => ({
          ...prev,
          [projectId]: response.data.progress || []
        }));
      }
    } catch (error) {
      console.error('获取项目进度失败:', error);
    }
  };

  // 加载所有项目进度
  const fetchAllProgress = async () => {
    for (const project of projects) {
      await fetchProjectProgress(project.id);
    }
  };

  // 添加项目
  const handleAdd = () => {
    setCurrentProject(null);
    form.resetFields();
    setShowAddModal(true);
  };

  // 编辑项目
  const handleEdit = (project) => {
    setCurrentProject(project);
    form.setFieldsValue({
      ...project,
      start_date: project.start_date ? dayjs(project.start_date) : null,
      end_date: project.end_date ? dayjs(project.end_date) : null
    });
    setShowEditModal(true);
  };

  // 删除项目
  const handleDelete = async (projectId) => {
    try {
      const response = await axios.delete(`${projectsConfig.apiEndpoints.delete}/${projectId}`);
      if (response.data.success) {
        message.success('删除成功');
        fetchProjects();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除项目失败:', error);
      message.error('删除失败');
    }
  };

  // 保存项目
  const handleSaveProject = async (values) => {
    try {
      let response;
      const requestData = {
        ...values,
        start_date: values.start_date ? (dayjs.isDayjs(values.start_date) ? values.start_date.format('YYYY-MM-DD') : values.start_date) : null,
        end_date: values.end_date ? (dayjs.isDayjs(values.end_date) ? values.end_date.format('YYYY-MM-DD') : values.end_date) : null
      };

      if (currentProject) {
        response = await axios.put(`${projectsConfig.apiEndpoints.update}/${currentProject.id}`, requestData);
      } else {
        response = await axios.post(projectsConfig.apiEndpoints.create, requestData);
      }

      if (response.data.success) {
        message.success(currentProject ? '更新成功' : '添加成功');
        setShowAddModal(false);
        setShowEditModal(false);
        fetchProjects();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存项目失败:', error);
      message.error('操作失败');
    }
  };

  // 查看项目进度
  const handleViewProgress = (project) => {
    setCurrentProject(project);
    setShowProgressModal(true);
  };

  // 添加项目进度
  const handleAddProgress = (project) => {
    setCurrentProject(project);
    progressForm.resetFields();
    setShowProgressModal(true);
  };

  // 保存项目进度
  const handleSaveProgress = async (values) => {
    try {
      const response = await axios.post(projectsConfig.apiEndpoints.addProgress, {
        project_id: currentProject.id,
        ...values
      });

      if (response.data.success) {
        message.success('进度记录添加成功');
        progressForm.resetFields();
        fetchProjectProgress(currentProject.id);
      } else {
        message.error(response.data.message || '添加失败');
      }
    } catch (error) {
      console.error('添加项目进度失败:', error);
      message.error('添加失败');
    }
  };

  // 删除项目进度
  const handleDeleteProgress = async (progressId) => {
    try {
      const response = await axios.delete(`${projectsConfig.apiEndpoints.deleteProgress}/${progressId}`);
      if (response.data.success) {
        message.success('进度记录删除成功');
        fetchProjectProgress(currentProject.id);
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除项目进度失败:', error);
      message.error('删除失败');
    }
  };

  // 计算项目进度百分比
  const calculateProgress = (project) => {
    const progress = projectProgress[project.id] || [];
    if (progress.length === 0) return 0;
    
    const totalProgress = progress.reduce((sum, p) => sum + (p.progress_percentage || 0), 0);
    return Math.min(100, Math.round(totalProgress / progress.length));
  };

  // 获取状态图标
  const getStatusIcon = (status) => {
    switch (status) {
      case 'planning':
        return <ClockCircleOutlined style={{ color: '#1890FF' }} />;
      case 'executing':
        return <PlayCircleOutlined style={{ color: '#52C41A' }} />;
      case 'monitoring':
        return <BarChartOutlined style={{ color: '#FAAD14' }} />;
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52C41A' }} />;
      case 'paused':
        return <PauseCircleOutlined style={{ color: '#FF4D4F' }} />;
      case 'cancelled':
        return <ExclamationCircleOutlined style={{ color: '#666666' }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  // 获取风险等级颜色
  const getRiskColor = (riskLevel) => {
    const risk = projectsConfig.riskLevels.find(r => r.value === riskLevel);
    return risk?.color || '#666666';
  };

  // 组件加载时获取数据
  useEffect(() => {
    initialize();
    fetchProjects();
  }, [initialize]);

  // 当项目数据更新时，加载项目进度
  useEffect(() => {
    if (projects.length > 0) {
      fetchAllProgress();
    }
  }, [projects]);

  return (
    <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div>
          <Space align="center" size={12} style={{ marginBottom: 6 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/')}
            >
              返回
            </Button>
            <h1 className="mod-header-title">
              <ProjectOutlined style={{ marginRight: 8, color: projectsConfig.bgColor }} />
              {projectsConfig.name}
            </h1>
          </Space>
          <div className="mod-header-subtitle">计划、执行、监控 —— 全流程跟踪项目进度、投入与风险</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          style={{ backgroundColor: projectsConfig.bgColor, borderColor: projectsConfig.bgColor }}
        >
          新增项目
        </Button>
      </div>

      {/* 统计卡 */}
      {projects.length > 0 && (
        <div className="mod-stats">
          {[
            { key: 'total', label: '项目总数', value: projects.length, icon: <ProjectOutlined />, color: '#6366F1' },
            { key: 'executing', label: '执行中', value: projects.filter((p) => p.status === 'executing').length, icon: <PlayCircleOutlined />, color: '#52C41A' },
            { key: 'completed', label: '已完成', value: projects.filter((p) => p.status === 'completed').length, icon: <CheckCircleOutlined />, color: '#1890FF' },
            { key: 'highRisk', label: '高风险', value: projects.filter((p) => p.risk_level === 'high').length, icon: <ExclamationCircleOutlined />, color: '#FF4D4F', danger: projects.filter((p) => p.risk_level === 'high').length > 0 },
          ].map((s) => (
            <div key={s.key} className={`mod-stat-card${s.danger ? ' danger' : ''}`}>
              <div className="mod-stat-icon" style={{ color: s.color }}>{s.icon}</div>
              <div>
                <div className="mod-stat-value" style={{ color: s.danger ? '#f5222d' : undefined }}>{s.value}</div>
                <div className="mod-stat-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 主要内容 */}
      <div>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'projects',
              label: '项目列表',
              children: (
                <Spin spinning={loading}>
                  {!loading && projects.length === 0 ? (
                    <Empty
                      description="暂无项目，点击「新增项目」按钮开始添加"
                      style={{ padding: '60px 0' }}
                    >
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                        style={{ backgroundColor: projectsConfig.bgColor, borderColor: projectsConfig.bgColor }}
                      >
                        新增项目
                      </Button>
                    </Empty>
                  ) : (
                    <Row gutter={[16, 16]}>
                      {projects.map((project) => {
                        const progress = projectProgress[project.id] || [];
                        const progressPercentage = calculateProgress(project);
                        const status = projectsConfig.projectStatus.find(s => s.value === project.status);
                        const risk = projectsConfig.riskLevels.find(r => r.value === project.risk_level);
                        const themeColor = status?.color || '#6366F1';

                        return (
                          <Col xs={24} sm={12} md={8} lg={8} xl={6} key={project.id}>
                            <div className="mod-card">
                              {/* 项目标题和状态 */}
                              <div className="mod-card-head">
                                <div
                                  className="mod-card-icon"
                                  style={{ background: `${themeColor}1a`, color: themeColor }}
                                >
                                  <ProjectOutlined />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <Tooltip title={project.name}>
                                    <div className="mod-card-title">{project.name}</div>
                                  </Tooltip>
                                  <div className="mod-card-sub" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    {getStatusIcon(project.status)}
                                    <Tag color={status?.color} style={{ marginInlineEnd: 0 }}>{status?.label}</Tag>
                                    <Tag color="blue" style={{ marginInlineEnd: 0 }}>{project.type}</Tag>
                                  </div>
                                </div>
                              </div>

                              {/* 项目信息 */}
                              <div className="mod-card-body">
                                <Row gutter={[8, 8]}>
                                  <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>投资：</Text>
                                    <Text strong style={{ fontSize: 13 }}>¥{project.investment || '0'}</Text>
                                  </Col>
                                  <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>人员：</Text>
                                    <Text style={{ fontSize: 13 }}>{project.team_size || '未设置'}</Text>
                                  </Col>
                                  <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>预期收益：</Text>
                                    <Text strong style={{ color: '#52C41A', fontSize: 13 }}>¥{project.expected_revenue || '0'}</Text>
                                  </Col>
                                  <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>风险：</Text>
                                    <Tag color={getRiskColor(project.risk_level)} style={{ marginInlineEnd: 0 }}>
                                      {risk?.label || '未设置'}
                                    </Tag>
                                  </Col>
                                </Row>
                                <div style={{ marginTop: 8, fontSize: 12 }}>
                                  <ClockCircleOutlined style={{ color: '#8c8c8c', marginInlineEnd: 4 }} />
                                  <Text type="secondary">{project.start_date || '未设置'} ~ {project.end_date || '未设置'}</Text>
                                </div>
                                {project.description && (
                                  <div style={{ marginTop: 8 }}>
                                    <Text type="secondary" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}>
                                      {project.description}
                                    </Text>
                                  </div>
                                )}

                                {/* 项目进度 */}
                                <div style={{ marginTop: 10 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>项目进度</Text>
                                    <Text style={{ fontSize: 12, color: themeColor }}>{progressPercentage}%</Text>
                                  </div>
                                  <Progress
                                    percent={progressPercentage}
                                    size="small"
                                    strokeColor={themeColor}
                                    showInfo={false}
                                  />
                                </div>
                              </div>

                              {/* 操作按钮 */}
                              <div className="mod-card-actions">
                                <Badge count={progress.length} size="small">
                                  <Tooltip title="进度记录">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<TrophyOutlined />}
                                      onClick={() => handleViewProgress(project)}
                                    />
                                  </Tooltip>
                                </Badge>
                                <Space size={0}>
                                  <Tooltip title="编辑">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<EditOutlined />}
                                      onClick={() => handleEdit(project)}
                                    />
                                  </Tooltip>
                                  <Tooltip title="添加进度">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<PlusOutlined />}
                                      onClick={() => handleAddProgress(project)}
                                    />
                                  </Tooltip>
                                  <Popconfirm
                                    title="确定要删除这个项目吗？"
                                    onConfirm={() => handleDelete(project.id)}
                                    okText="确定"
                                    cancelText="取消"
                                  >
                                    <Tooltip title="删除">
                                      <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                      />
                                    </Tooltip>
                                  </Popconfirm>
                                </Space>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </Spin>
              )
            },
            {
              key: 'process',
              label: '五大流程',
              children: (
                <Card title={projectsConfig.projectManagementProcess.title}>
                  {projectsConfig.projectManagementProcess.sections.map((section, index) => (
                    <div key={index} style={{ marginBottom: '24px' }}>
                      <Title level={4} style={{ color: projectsConfig.bgColor }}>
                        {section.title}
                      </Title>
                      <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                        {section.subtitle}
                      </Text>
                      <ul style={{ paddingLeft: '20px' }}>
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ marginBottom: '8px' }}>
                            <Text>{item}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <Divider />
                  <div style={{ textAlign: 'center', padding: '16px' }}>
                    <Text strong style={{ fontSize: '16px', color: projectsConfig.bgColor }}>
                      {projectsConfig.projectManagementProcess.summary}
                    </Text>
                  </div>
                </Card>
              )
            },
            {
              key: 'methodologies',
              label: '方法论',
              children: (
                <Card title={projectsConfig.projectMethodologies.title}>
                  {projectsConfig.projectMethodologies.sections.map((section, index) => (
                    <div key={index} style={{ marginBottom: '24px' }}>
                      <Title level={4} style={{ color: projectsConfig.bgColor }}>
                        {section.title}
                      </Title>
                      <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                        {section.subtitle}
                      </Text>
                      <ul style={{ paddingLeft: '20px' }}>
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ marginBottom: '8px' }}>
                            <Text>{item}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </Card>
              )
            },
            {
              key: 'tips',
              label: '管理要点',
              children: (
                <Card title={projectsConfig.managementTips.title}>
                  {projectsConfig.managementTips.sections.map((section, index) => (
                    <div key={index} style={{ marginBottom: '24px' }}>
                      <Title level={4} style={{ color: projectsConfig.bgColor }}>
                        {section.title}
                      </Title>
                      <ul style={{ paddingLeft: '20px' }}>
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ marginBottom: '8px' }}>
                            <Text>{item}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </Card>
              )
            },
            {
              key: 'tools',
              label: '工具推荐',
              children: (
                <Card title={projectsConfig.projectTools.title}>
                  {projectsConfig.projectTools.sections.map((section, index) => (
                    <div key={index} style={{ marginBottom: '24px' }}>
                      <Title level={4} style={{ color: projectsConfig.bgColor }}>
                        {section.title}
                      </Title>
                      <ul style={{ paddingLeft: '20px' }}>
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ marginBottom: '8px' }}>
                            <Text>{item}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </Card>
              )
            }
          ]}
        />
      </div>

      {/* 添加/编辑项目弹窗 */}
      {(showAddModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '700px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                {currentProject ? '编辑项目' : '新增项目'}
              </h3>
              <Button
                type="text"
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveProject}
        >
          <Form.Item
            name="name"
            label="项目名称"
            rules={[{ required: true, message: '请输入项目名称' }]}
          >
            <Input placeholder="请输入项目名称" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="项目类型"
                rules={[{ required: true, message: '请选择项目类型' }]}
              >
                <Select placeholder="请选择项目类型">
                  {projectsConfig.projectTypes.map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="项目状态"
                rules={[{ required: true, message: '请选择项目状态' }]}
              >
                <Select placeholder="请选择项目状态">
                  {projectsConfig.projectStatus.map(status => (
                    <Option key={status.value} value={status.value}>
                      <Tag color={status.color}>{status.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="investment"
                label="投资金额"
              >
                <InputNumber 
                  placeholder="投资金额" 
                  style={{ width: '100%' }}
                  addonBefore="¥"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="expected_revenue"
                label="预期收益"
              >
                <InputNumber 
                  placeholder="预期收益" 
                  style={{ width: '100%' }}
                  addonBefore="¥"
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="team_size"
                label="人员规模"
              >
                <Select placeholder="请选择人员规模">
                  {projectsConfig.teamSizes.map(size => (
                    <Option key={size} value={size}>{size}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="risk_level"
                label="风险程度"
              >
                <Select placeholder="请选择风险程度">
                  {projectsConfig.riskLevels.map(risk => (
                    <Option key={risk.value} value={risk.value}>
                      <Tag color={risk.color}>{risk.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="start_date"
                label="开始时间"
              >
                <DatePicker style={{ width: '100%' }} placeholder="选择开始时间" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="end_date"
                label="结束时间"
              >
                <DatePicker style={{ width: '100%' }} placeholder="选择结束时间" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="description"
            label="项目描述"
          >
            <TextArea rows={3} placeholder="请输入项目描述" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {currentProject ? '更新' : '添加'}
              </Button>
              <Button onClick={() => {
                setShowAddModal(false);
                setShowEditModal(false);
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
            
          </div>
        </div>
      )}

      {/* 项目进度弹窗 */}
      {showProgressModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                {currentProject?.name || ''} - 项目进度
              </h3>
              <Button
                type="text"
                onClick={() => setShowProgressModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
        <div style={{ marginBottom: '16px' }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => progressForm.resetFields()}
            style={{ backgroundColor: projectsConfig.bgColor, borderColor: projectsConfig.bgColor }}
          >
            添加进度
          </Button>
        </div>
        
        <Form
          form={progressForm}
          layout="vertical"
          onFinish={handleSaveProgress}
        >
          <Form.Item
            name="content"
            label="进度内容"
            rules={[{ required: true, message: '请输入进度内容' }]}
          >
            <TextArea rows={3} placeholder="请输入项目进度内容" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="progress_percentage"
                label="进度百分比"
                rules={[{ required: true, message: '请输入进度百分比' }]}
              >
                <InputNumber 
                  min={0} 
                  max={100} 
                  placeholder="0-100" 
                  style={{ width: '100%' }}
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="cost"
                label="成本支出"
              >
                <InputNumber 
                  min={0} 
                  placeholder="成本支出" 
                  style={{ width: '100%' }}
                  addonBefore="¥"
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="notes"
            label="进度备注"
          >
            <TextArea rows={4} placeholder="记录项目进展、遇到的问题等" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                保存进度
              </Button>
              <Button onClick={() => progressForm.resetFields()}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
        
        <div style={{ marginTop: '24px' }}>
          <Title level={5}>历史进度</Title>
          <List
            dataSource={currentProject ? (projectProgress[currentProject.id] || []) : []}
            renderItem={(progress) => (
              <List.Item
                actions={[
                  <Popconfirm
                    title="确定要删除这条进度记录吗？"
                    onConfirm={() => handleDeleteProgress(progress.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Tooltip title="删除">
                      <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <CalendarOutlined />
                      <Text>{new Date(progress.created_at).toLocaleString('zh-CN')}</Text>
                      <Tag color="blue">{progress.progress_percentage}%</Tag>
                      {progress.cost && <Tag color="green">¥{progress.cost}</Tag>}
                    </Space>
                  }
                  description={
                    <div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong>进度内容：</Text>
                        <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{progress.content}</Text>
                      </div>
                      {progress.notes && (
                        <div>
                          <Text strong>进度备注：</Text>
                          <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{progress.notes}</Text>
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
